/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

/* =====================================================================
   NOSKIN CUSTOMISATIONS MANIFEST
   ---------------------------------------------------------------------
   READ THIS BEFORE ANY THEME UPDATE OR MIGRATION.
   Shopify theme updates DO NOT carry code edits. Every custom change
   in this theme is listed here. Re-apply each item to the new theme
   (structures may differ between versions - port the INTENT, and check
   each target file's new shape before pasting).

   Last updated: 13 Aug 2026 (Claude + Tony)

   1. assets/custom.js  (THIS FILE)
      Size Format Toggle: EU / Mens / Womens size relabelling with
      localStorage preference, re-render-proof (capture-phase listener
      + MutationObserver), plus properties[Sizing] line-item property
      so cart/checkout/orders show the shopper's chosen format.
      Self-contained - copy the whole IIFE.

   2. layout/theme.liquid
      TWO ONE-LINE EDITS:
      (a) the custom.js <script> import near the bottom is uncommented
          (fresh theme copies ship with it commented out - if the size
          toggle is dead after an update, check this FIRST).
      (b) custom.css stylesheet_tag added in <head>, after
          content_for_header (see item 6).

   3. snippets/product-variant-picker.liquid
      (a) Three metafield assigns near top of opening liquid block:
          sizing_eu / sizing_mens / sizing_womens from
          product.metafields.sizing.*
      (b) Size-format <select> + window.productSizing <script> block,
          inserted in the BUTTON picker branch after </legend>,
          gated: {%- if is_size_option and sizing_eu != blank -%}
      (c) Size option legend suppressed: option-name span and
          selected-value <small> wrapped in unless is_size_option
          (size chart link still renders; other options untouched)

   4. snippets/product-variant-options.liquid
      data-size-btn data-index="{{ forloop.index0 }}" attributes on
      the option-title span in the button branch (JS relabel hooks).

   5. sections/noskin-marquee.liquid  (NEW FILE - self-contained)
      Infinite scrolling text marquee ('Nōskin Marquee' in the theme
      editor). Pure CSS animation, all settings in schema, respects
      prefers-reduced-motion. Migration = copy the whole file.

   6. assets/custom.css  (NEW FILE)
      All custom CSS. Loaded from layout/theme.liquid, see item 2(b).
      READ THAT FILE'S OWN COMMENT BLOCK for what each section does and
      why - it is authoritative and self-documenting, so this entry does
      not need updating when rules are added. Migration = copy the whole
      file, plus the one-line stylesheet_tag in theme.liquid.

   ---------------------------------------------------------------------
   OPEN ISSUE - HEADER CART COUNT IS STALE (not yet fixed)

   UpCart (data-upcart-cart-enabled on <body>) owns add-to-cart and runs
   its own drawer. It never fires Broadcast's cart events, so the
   theme's <cart-count> element only reflects the cart as it was when
   the page loaded. Add an item and the header bag badge does not move
   until the next page load. This is PRE-EXISTING and also affects the
   live theme - it is easy to miss behind the bag glyph.
   Disabling the UpCart app embed on a theme returns the native Shopify
   cart and should resolve it. If UpCart is removed for good, note that
   its free-shipping progress bar goes with it - Broadcast has its own
   free-shipping message block in group-header, currently disabled.

   Attempted 13 Aug 2026 and REVERTED, all of it:
   - Replacing the bag glyph with a CART: n text label
     (snippets/header-icon.liquid + custom.css). Made the staleness
     obvious rather than causing it.
   - Emptying data-status-separator from JS. The component then renders
     nothing at all, the theme hides the empty count, and the label
     collapses to bare CART. DO NOT RETRY.
   - Syncing the count from /cart.js on load and on cart-endpoint
     requests, by wrapping window.fetch and XMLHttpRequest.send.
     Did not work against UpCart. Removed - do not leave a fetch
     wrapper in the cart path without proof it works.

   ALSO OPEN - CART DRAWER SETTING HAS NO EFFECT
   Setting the cart type to 'drawer' still opens /cart as a page.
   sections/cart-drawer.liquid exists in the theme but nothing renders
   it: sections/group-overlay.json contains only the text promo and
   newsletter popup, and layout/theme.liquid renders no cart drawer.
   Likely a lineage artefact - this theme is a copy chain going back to
   the Jan 2024 release, and existing themes would not have gained the
   section group entry when Broadcast moved the drawer into one.
   FIX: add the Cart drawer section to the Overlay group in the theme
   editor, or write the entry into sections/group-overlay.json.
   ---------------------------------------------------------------------

   DEPENDENCIES (not code, but the system dies without them):
   - Product metafields: sizing.eu_sizes, sizing.mens_sizes,
     sizing.womens_sizes (list types, index-aligned with the EU
     variant option order) on footwear products.
   - Variant option values ARE the EU sizes (source of truth).

   PLANNED ADDITIONS (add to this manifest as built):
   - Lookbook hotspots (note: Broadcast ships section-look.liquid -
     evaluate before building custom)
   - Footer rebuild (flat support links + inline newsletter). Broadcast
     runs the footer as a static section inside group-footer - build
     against sections/footer.liquid, NOT as a Dawn-style section.
   ===================================================================== */


(function() {
  // Add custom code below this line
  // =============================================
  // Size Format Toggle (re-render-proof for variant-selects)
  // + line-item property so cart/checkout show the chosen format
  // =============================================
  const STORAGE_KEY = 'preferredSizeFormat';

  function currentFormat() {
    return localStorage.getItem(STORAGE_KEY) || 'eu';
  }

  function formatDisplayName(format) {
    if (format === 'mens') return 'Mens US / AUS';
    if (format === 'womens') return 'Womens US / AUS';
    return 'EU';
  }

  function syncSizingProperty(format) {
    const sizing = window.productSizing;
    if (!sizing) return;

    document.querySelectorAll('[data-size-btn]').forEach(function (span) {
      const label = span.closest('label');
      if (!label) return;
      const input = document.getElementById(label.getAttribute('for'));
      if (!input || !input.checked) return;

      const formId = input.getAttribute('form');
      if (!formId) return;

      let prop = document.querySelector('input[data-sizing-property][form="' + formId + '"]');

      // In EU mode the variant already says it all - carry no property
      if (format === 'eu') {
        if (prop) prop.remove();
        return;
      }

      const index = parseInt(span.getAttribute('data-index'), 10);
      const labels = sizing[format];
      if (!labels || labels[index] === undefined) return;

      if (!prop) {
        prop = document.createElement('input');
        prop.type = 'hidden';
        prop.name = 'properties[Sizing]';
        prop.setAttribute('form', formId);
        prop.setAttribute('data-sizing-property', '');
        input.insertAdjacentElement('afterend', prop);
      }

      prop.value = labels[index] + ' (' + formatDisplayName(format) + ')';
    });
  }

  function applyFormat(format) {
    const sizing = window.productSizing;
    if (!sizing || !sizing[format]) return;
    const labels = sizing[format];

    document.querySelectorAll('[data-size-btn]').forEach(function (item) {
      const index = parseInt(item.getAttribute('data-index'), 10);
      if (labels[index] !== undefined && item.textContent !== labels[index]) {
        item.textContent = labels[index];
      }
    });

    const select = document.getElementById('size-format-select');
    if (select && select.value !== format) {
      select.value = format;
    }

    syncSizingProperty(format);
  }

  // Delegated listener in CAPTURE phase: fires before the theme's
  // variant-selects component sees the event. For the format select we
  // stopPropagation so the theme never re-renders; for size radios we
  // let the event through but resync the property afterwards.
  document.addEventListener('change', function (e) {
    if (!e.target) return;

    if (e.target.id === 'size-format-select') {
      e.stopPropagation();
      const format = e.target.value;
      localStorage.setItem(STORAGE_KEY, format);
      applyFormat(format);
      return;
    }

    // A size radio changed: keep the cart property in step
    if (e.target.matches('input[type="radio"]')) {
      setTimeout(function () {
        syncSizingProperty(currentFormat());
      }, 0);
    }
  }, true);

  // Re-apply after ANY theme re-render (variant change, quick-add, etc).
  // Guarded so it settles: applyFormat only mutates when labels differ.
  let raf;
  const observer = new MutationObserver(function () {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function () {
      if (document.querySelector('[data-size-btn]')) {
        applyFormat(currentFormat());
      }
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () {
      applyFormat(currentFormat());
    }, 300);
  });

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
